import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  MapPin, Navigation, Phone, MessageCircle, Clock, Loader2,
  CheckCircle, Truck, Store, Home, Share2, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';
import useAnimatedPosition from '@/hooks/useAnimatedPosition';

// Fix do ícone padrão do Leaflet com Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// SVG inline para os ícones — laranja do brand Rota 017
// O SVG da moto naturalmente "aponta" para a direita (Leste = 90°).
// Logo, para apontar na direção do movimento basta rotacionar (bearing - 90°).
const buildIconeMoto = (rotationDeg = 0) => L.divIcon({
  className: 'custom-bike-icon',
  html: `<div style="background:#ea580c;border-radius:50%;width:48px;height:48px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(234,88,12,0.5);border:3px solid white;animation: pulse-orange 2s infinite;">
    <span style="display:flex;transform:rotate(${rotationDeg}deg);transition:transform 1s ease-out;">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 12c-1.49 0-2.87.47-4 1.27V11l4-4h-3V5h-2v2h-3l-2 2H7c-1.66 0-3 1.34-3 3v3l5 1v3l3-3-2.59-2.59c-.78-.78-.78-2.05 0-2.83.78-.78 2.05-.78 2.83 0L14 11c.93-1.21 2.39-2 4-2 2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5h2c0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3z"/>
        <circle cx="6" cy="18" r="2.5" stroke="white" stroke-width="0.5"/>
      </svg>
    </span>
  </div>
  <style>@keyframes pulse-orange { 0%,100%{transform:scale(1);} 50%{transform:scale(1.1);} }</style>`,
  iconSize: [48, 48],
  iconAnchor: [24, 24],
});

const iconeOrigem = L.divIcon({
  className: 'custom-origin-icon',
  html: `<div style="background:#16a34a;border-radius:50% 50% 50% 0;width:36px;height:36px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style="transform:rotate(45deg);"><path d="M12 2L2 12h3v8h6v-6h2v6h6v-8h3L12 2z"/></svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const iconeDestino = L.divIcon({
  className: 'custom-destination-icon',
  html: `<div style="background:#dc2626;border-radius:50% 50% 50% 0;width:36px;height:36px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style="transform:rotate(45deg);"><path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/></svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

// Componente que ajusta o zoom para enquadrar todos os pontos
const FitBounds = ({ pontos }) => {
  const map = useMap();
  useEffect(() => {
    if (pontos.length === 0) return;
    const validos = pontos.filter(p => p && p[0] && p[1]);
    if (validos.length === 0) return;
    if (validos.length === 1) {
      map.setView(validos[0], 15);
    } else {
      map.fitBounds(validos, { padding: [50, 50] });
    }
  }, [pontos, map]);
  return null;
};

const MapaEntrega = ({ entregaId, isOpen, onClose }) => {
  const [tracking, setTracking] = useState(null);
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const pollingRef = useRef(null);
  const [posicaoEntregador, setPosicaoEntregador] = useState(null);
  
  // Carrega config do mapa uma vez
  useEffect(() => {
    api.get('/maps/config').then(res => setMapConfig(res.data)).catch(() => setMapConfig({ provider: 'osm', default_center: [-23.5505, -46.6333], default_zoom: 13 }));
  }, []);
  
  // Snapshot inicial via REST + WebSocket para atualizações
  useEffect(() => {
    if (!isOpen || !entregaId) return;
    let isMounted = true;
    setLoading(true);
    
    const carregarSnapshot = async () => {
      try {
        const res = await api.get(`/entregas/${entregaId}/tracking`);
        if (!isMounted) return;
        setTracking(res.data);
        if (res.data.entregador_posicao) {
          setPosicaoEntregador([res.data.entregador_posicao.latitude, res.data.entregador_posicao.longitude]);
        }
      } catch {
        toast.error('Erro ao carregar tracking');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    carregarSnapshot();
    
    // WebSocket para updates em tempo real
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/ws/tracking/${entregaId}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.tipo === 'posicao_atualizada') {
            setPosicaoEntregador([data.latitude, data.longitude]);
            setTracking((prev) => prev ? {
              ...prev,
              entregador_posicao: {
                latitude: data.latitude,
                longitude: data.longitude,
                atualizado_em: data.atualizado_em,
              },
            } : prev);
          } else if (data.tipo === 'snapshot') {
            setTracking(data);
            if (data.entregador_posicao) {
              setPosicaoEntregador([data.entregador_posicao.latitude, data.entregador_posicao.longitude]);
            }
          }
        } catch (e) {
          // Ignora mensagens não-JSON
        }
      };
      ws.onerror = () => {
        // Fallback para polling se WS falhar
      };
    } catch {
      // WS pode não estar disponível, usa polling
    }
    
    // Polling de fallback a cada 15s (segurança caso WebSocket falhe)
    pollingRef.current = setInterval(carregarSnapshot, 15000);
    
    return () => {
      isMounted = false;
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { /* noop */ }
      }
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isOpen, entregaId]);
  
  // Interpola a posição do entregador para movimento suave (estilo Uber)
  // e calcula bearing para girar o ícone na direção do trajeto.
  // (chamado ANTES do early return para respeitar Rules of Hooks)
  const { position: posicaoAnimada, bearing } = useAnimatedPosition(posicaoEntregador, 1200);
  // SVG da moto aponta para a direita por padrão (90°), por isso subtraímos 90.
  const iconeMoto = useMemo(() => buildIconeMoto(bearing - 90), [bearing]);
  
  if (!isOpen) return null;
  
  const entrega = tracking?.entrega;
  const entregador = tracking?.entregador;
  const rota = tracking?.rota;
  
  const origem = entrega?.origem_latitude && entrega?.origem_longitude ? [entrega.origem_latitude, entrega.origem_longitude] : null;
  const destino = entrega?.destino_latitude && entrega?.destino_longitude ? [entrega.destino_latitude, entrega.destino_longitude] : null;
  
  const pontos = [origem, destino, posicaoEntregador].filter(Boolean);
  const center = mapConfig?.default_center || [-23.5505, -46.6333];
  
  const tileUrl = mapConfig?.geoapify_key
    ? `https://maps.geoapify.com/v1/tile/osm-bright/{z}/{x}/{y}.png?apiKey=${mapConfig.geoapify_key}`
    : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  const tileAttribution = mapConfig?.geoapify_key
    ? '© Geoapify © OpenStreetMap contributors'
    : '© OpenStreetMap contributors';
  
  const statusLabel = {
    'criada': { text: 'Aguardando entregador', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    'aceita': { text: 'Entregador aceitou', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
    'atribuida': { text: 'Entregador a caminho', color: 'bg-blue-100 text-blue-800', icon: Truck },
    'em_rota': { text: 'Em rota com o pedido', color: 'bg-orange-100 text-orange-800', icon: Navigation },
    'entregue': { text: 'Entregue com sucesso', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    'cancelada': { text: 'Cancelada', color: 'bg-red-100 text-red-800', icon: X },
  }[entrega?.status] || { text: entrega?.status || '...', color: 'bg-slate-100 text-slate-800', icon: Clock };
  
  const StatusIcon = statusLabel.icon;
  
  const telefoneEntregador = (entregador?.telefone || '').replace(/\D/g, '');
  const msgWa = `Olá! Sou o cliente da entrega "${entrega?.descricao || ''}". Estou acompanhando pelo mapa.`;
  
  const compartilharLink = async () => {
    try {
      const res = await api.post(`/entregas/${entregaId}/tracking-link`);
      const fullUrl = window.location.origin + res.data.url;
      if (navigator.share) {
        await navigator.share({ title: 'Acompanhar entrega', text: 'Acompanhe sua entrega em tempo real', url: fullUrl });
      } else {
        await navigator.clipboard.writeText(fullUrl);
        toast.success('Link copiado para a área de transferência!');
      }
    } catch {
      toast.error('Não foi possível gerar o link');
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[95vh] p-0 overflow-hidden" data-testid="mapa-entrega-modal">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-orange-600" />
              Acompanhar Entrega
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={compartilharLink} data-testid="btn-share-tracking">
              <Share2 className="w-4 h-4 mr-1" /> Compartilhar
            </Button>
          </div>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 h-[70vh]">
            {/* Mapa */}
            <div className="md:col-span-2 relative bg-slate-100">
              <MapContainer
                center={center}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer url={tileUrl} attribution={tileAttribution} />
                
                {origem && (
                  <Marker position={origem} icon={iconeOrigem}>
                    <Popup>
                      <strong>Origem (loja)</strong><br />
                      {entrega.origem_endereco || ''}
                    </Popup>
                  </Marker>
                )}
                
                {destino && (
                  <Marker position={destino} icon={iconeDestino}>
                    <Popup>
                      <strong>Destino</strong><br />
                      {entrega.destino_endereco || ''}
                    </Popup>
                  </Marker>
                )}
                
                {posicaoAnimada && (
                  <Marker position={posicaoAnimada} icon={iconeMoto}>
                    <Popup>
                      <strong>{entregador?.nome || 'Entregador'}</strong><br />
                      Posição em tempo real
                    </Popup>
                  </Marker>
                )}
                
                {rota?.polyline && rota.polyline.length > 1 && (
                  <Polyline
                    positions={rota.polyline}
                    pathOptions={{ color: '#ea580c', weight: 5, opacity: 0.8, dashArray: '0' }}
                  />
                )}
                
                <FitBounds pontos={pontos} />
              </MapContainer>
            </div>
            
            {/* Painel lateral */}
            <div className="md:col-span-1 p-4 overflow-y-auto bg-white space-y-4">
              {/* Status */}
              <div className={`rounded-xl p-3 flex items-center gap-3 ${statusLabel.color}`} data-testid="tracking-status">
                <StatusIcon className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-semibold">{statusLabel.text}</p>
              </div>
              
              {/* ETA */}
              {rota && entrega?.status === 'em_rota' && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3" data-testid="tracking-eta">
                  <p className="text-xs text-orange-700 mb-1">Tempo estimado de chegada</p>
                  <p className="text-3xl font-bold text-orange-800">
                    {rota.duracao_min} min
                  </p>
                  <p className="text-xs text-orange-600 mt-1">{rota.distancia_km.toFixed(1)} km até você</p>
                </div>
              )}
              
              {/* Entregador */}
              {entregador && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-3" data-testid="tracking-entregador">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <Truck className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{entregador.nome || 'Entregador'}</p>
                      <p className="text-xs text-slate-500">{entregador.razao_social}</p>
                    </div>
                  </div>
                  
                  {telefoneEntregador && (
                    <div className="grid grid-cols-2 gap-2">
                      <a
                        href={`tel:+${telefoneEntregador}`}
                        className="flex items-center justify-center gap-1 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-sm font-medium text-slate-700 transition-colors"
                        data-testid="btn-call-entregador"
                      >
                        <Phone className="w-4 h-4" /> Ligar
                      </a>
                      <a
                        href={`https://wa.me/${telefoneEntregador}?text=${encodeURIComponent(msgWa)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-sm font-medium text-white transition-colors"
                        data-testid="btn-whatsapp-entregador"
                      >
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                      </a>
                    </div>
                  )}
                </div>
              )}
              
              {/* Endereços */}
              <div className="space-y-3 text-sm">
                {entrega?.origem_endereco && (
                  <div className="flex items-start gap-2">
                    <Store className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Origem</p>
                      <p className="text-slate-900">{entrega.origem_endereco}</p>
                    </div>
                  </div>
                )}
                {entrega?.destino_endereco && (
                  <div className="flex items-start gap-2">
                    <Home className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Destino</p>
                      <p className="text-slate-900">{entrega.destino_endereco}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Última atualização */}
              {tracking?.entregador_posicao?.atualizado_em && (
                <p className="text-xs text-slate-400 text-center pt-2 border-t">
                  Localização atualizada: {new Date(tracking.entregador_posicao.atualizado_em).toLocaleTimeString('pt-BR')}
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MapaEntrega;
